import { derived, type Readable } from 'svelte/store';
import { createMutation, createQuery } from '$lib/api/tanstack-query';

import {
  SandboxState,
  type SimSandbox as APISimSandbox,
  DestroyReason,
  TerminationCause,
} from '@viamrobotics/web-client/simsandbox/v1/simsandbox';

import { assertExists } from '$lib/assert';
import { useAuthContext } from '$lib/auth';

import { useClient } from './client';
import { useQueryInvalidator } from './invalidate';
import { createQueryOptions, useAuthedQueryOptions } from './query-options';
import { machineKeys } from './robots/query-key';
import { timestampToDate } from './timestamp';

export {
  SandboxState,
  TerminationCause,
} from '@viamrobotics/web-client/simsandbox/v1/simsandbox';

/** A part's most recently started simulation sandbox. */
export interface SimSandbox {
  sandboxID: string;
  partID: string;
  /**
   * As recorded by the backend rather than asked of the provider, so a
   * sandbox reads as provisioning until it is seen running, and as started
   * until an exit on its own is detected.
   */
  state: SandboxState;
  startedAt: Date | undefined;
  /** Set once the sandbox has failed or terminated. */
  terminationCause: TerminationCause;
}

/** The provider has the sandbox, so it can be stopped, whether or not it is running yet. */
export const isSandboxStoppable = (state: SandboxState | undefined) =>
  state === SandboxState.PROVISIONING || state === SandboxState.STARTED;

/** A simulation sandbox's state as recorded by the backend. */
export interface SimSandboxStatus {
  /** False once the sandbox has failed, been destroyed, or exited on its own. */
  running: boolean;
  /** True once the backend has seen the provider running the sandbox's container. */
  started: boolean;
  startedAt: Date | undefined;
  /** Set once the sandbox has failed or terminated. */
  terminationCause: TerminationCause;
}

/** Past this long, a warm machine would usually already be handing the container over. */
const PROVISIONING_WARMING_AFTER_MS = 60_000;

/**
 * Matches data.SimulationContainerWarmStartThreshold. Past this, the machine
 * was not already up.
 */
export const PROVISIONING_WAITING_AFTER_MS = 3 * 60_000;

export type ProvisioningPace = 'normal' | 'warming' | 'waiting';

export const provisioningPace = (
  startedAt: Date | undefined,
  nowMs: number
): ProvisioningPace => {
  if (startedAt === undefined) return 'normal';
  const elapsed = nowMs - startedAt.getTime();
  if (elapsed > PROVISIONING_WAITING_AFTER_MS) return 'waiting';
  if (elapsed > PROVISIONING_WARMING_AFTER_MS) return 'warming';
  return 'normal';
};

export const provisioningElapsedMs = (
  startedAt: Date | undefined,
  nowMs: number
): number | undefined =>
  startedAt === undefined
    ? undefined
    : Math.max(0, nowMs - startedAt.getTime());

/** A short elapsed label, such as "<1 min" or "6 min". */
export const formatProvisioningElapsed = (elapsedMs: number): string => {
  const minutes = Math.floor(elapsedMs / 60_000);
  if (minutes < 1) return '<1 min';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} hr` : `${hours} hr ${rest} min`;
};

export const describeProvisioning = (pace: ProvisioningPace) => {
  switch (pace) {
    case 'warming': {
      return (
        "We're allocating a GPU-enabled machine in the cloud to run your simulation. " +
        'Warm machines are usually ready by now. Starting a new one takes longer.'
      );
    }
    case 'waiting': {
      return (
        "We're still waiting for a GPU-enabled machine. When many simulations start together, " +
        "this can take 10 to 20 minutes. We'll try for up to 1 hour."
      );
    }
    case 'normal': {
      return "We're allocating a GPU-enabled machine in the cloud to run your simulation.";
    }
  }
};

const TERMINATION_CAUSE_DESCRIPTION: Record<TerminationCause, string> = {
  [TerminationCause.UNSPECIFIED]: 'Your simulation instance was terminated.',
  [TerminationCause.UNKNOWN]: 'Your simulation instance was terminated.',
  [TerminationCause.USER]: 'Your simulation instance was stopped by a user.',
  [TerminationCause.IDLE]:
    'Your simulation instance was stopped automatically after an hour without any machine movement.',
  [TerminationCause.PROVIDER]:
    'Your simulation instance was shut down by its cloud host, for example after reaching its maximum run time.',
  [TerminationCause.EXITED]:
    'Your simulation instance stopped because the simulation inside it exited.',
  [TerminationCause.LAUNCH_FAILED]:
    'Your simulation instance could not be launched.',
  [TerminationCause.PART_DELETED]:
    'Your simulation instance was stopped because its part or machine was deleted.',
  [TerminationCause.CAPACITY]:
    'No GPU-enabled machine could be found within an hour. Please try again in a few minutes.',
};

export const describeTermination = (cause: TerminationCause) =>
  TERMINATION_CAUSE_DESCRIPTION[cause];

export interface CreateSimSandboxRequest {
  machineID: string;
  partID: string;
}

export interface CreateSimSandboxesRequest {
  machineID: string;
  partIDs: string[];
}

export interface DestroySimSandboxesRequest {
  machineID: string;
  sandboxIDs: string[];
}

const toSimSandbox = ({
  sandboxId,
  partId,
  state,
  startedAt,
  terminationCause,
}: APISimSandbox): SimSandbox => ({
  sandboxID: sandboxId,
  partID: partId,
  state,
  startedAt: startedAt ? timestampToDate(startedAt) : undefined,
  terminationCause,
});

/** Settle every request, then reject with all failures' messages if any failed. */
const settleAll = async (requests: Promise<unknown>[]) => {
  const results = await Promise.allSettled(requests);
  const failures = results.filter(
    (result): result is PromiseRejectedResult => result.status === 'rejected'
  );
  if (failures.length > 0) {
    throw new Error(
      failures.map(({ reason }) => (reason as Error).message).join('; ')
    );
  }
};

/** Get the most recently started simulation sandbox of each of a machine's parts. */
export const createGetMachineSimSandboxesQuery = (
  machineID: Readable<string | undefined>,
  enabled: Readable<boolean>,
  refetchIntervalMS?: number
) => {
  const baseQueryOptions = useAuthedQueryOptions('simSandbox');

  const options = derived(
    [baseQueryOptions, machineID, enabled],
    ([{ client, getRPCOptions, enabled: authed }, $machineID, $enabled]) =>
      createQueryOptions({
        queryKey: machineKeys.simSandboxes($machineID),
        enabled: authed && $enabled && $machineID !== undefined,
        queryFn: async () => {
          assertExists($machineID, 'Missing machine ID');

          const { response } = await client.getMachineSimSandboxes(
            { machineId: $machineID },
            await getRPCOptions()
          );

          return response.sandboxes.map(toSimSandbox);
        },
        refetchInterval: refetchIntervalMS,
      })
  );

  return createQuery(options);
};

/**
 * Get a simulation sandbox's recorded state, polling until it is no longer
 * running. Requires write access to the sandbox's part.
 */
export const createGetSimSandboxStatusQuery = (
  machineID: Readable<string>,
  sandboxID: Readable<string | undefined>,
  refetchIntervalMS: number
) => {
  const baseQueryOptions = useAuthedQueryOptions('simSandbox');

  const options = derived(
    [baseQueryOptions, machineID, sandboxID],
    ([{ client, getRPCOptions, enabled }, $machineID, $sandboxID]) =>
      createQueryOptions({
        queryKey: machineKeys.simSandboxStatus($machineID, $sandboxID),
        enabled: enabled && $sandboxID !== undefined,
        queryFn: async (): Promise<SimSandboxStatus> => {
          assertExists($sandboxID, 'Missing sandbox ID');

          const { response } = await client.getSimSandboxStatus(
            { sandboxId: $sandboxID },
            await getRPCOptions()
          );

          return {
            running:
              response.state === SandboxState.PENDING ||
              isSandboxStoppable(response.state),
            started: response.state === SandboxState.STARTED,
            startedAt: response.startedAt
              ? timestampToDate(response.startedAt)
              : undefined,
            terminationCause: response.terminationCause,
          };
        },
        refetchInterval: (query) =>
          query.state.data?.running === false ? false : refetchIntervalMS,
      })
  );

  return createQuery(options);
};

export interface SimSandboxPrice {
  /** In USD, or null when the org's billing tier does not price simulation. */
  usdPerMinute: number | null;
  /** Monthly USD credit shared across all cloud services in the org's billing tier. */
  monthlyCreditUSD: number;
}

/**
 * Get simulation pricing from the billing tier of the org the part bills.
 * Requires write access to the part.
 */
export const createGetSimSandboxPriceQuery = (
  machineID: Readable<string>,
  partID: Readable<string>
) => {
  const baseQueryOptions = useAuthedQueryOptions('simSandbox');

  const options = derived(
    [baseQueryOptions, machineID, partID],
    ([{ client, getRPCOptions, enabled }, $machineID, $partID]) =>
      createQueryOptions({
        queryKey: machineKeys.simSandboxPrice($machineID, $partID),
        enabled,
        queryFn: async (): Promise<SimSandboxPrice> => {
          const { response } = await client.getSimSandboxPrice(
            { partId: $partID },
            await getRPCOptions()
          );
          return {
            usdPerMinute: response.usdPerMinute ?? null,
            monthlyCreditUSD: response.monthlyCreditUsd,
          };
        },
      })
  );

  return createQuery(options);
};

/** Start a simulation sandbox bound to a part. Resolves to the new sandbox's ID. */
export const createCreateSimSandboxMutation = () => {
  const client = useClient('simSandbox');
  const { getRPCOptions } = useAuthContext();
  const { invalidateQueries } = useQueryInvalidator();

  return createMutation({
    mutationFn: async ({ partID }: CreateSimSandboxRequest) => {
      const { response } = await client.createSimSandbox(
        { partId: partID },
        await getRPCOptions()
      );
      return response.sandboxId;
    },
    onSuccess: async (_sandboxID, { machineID }) => {
      await invalidateQueries([machineKeys.simSandboxes(machineID)]);
    },
  });
};

/** Start a simulation sandbox for each of several parts, attempting all even if some fail. */
export const createCreateSimSandboxesMutation = () => {
  const client = useClient('simSandbox');
  const { getRPCOptions } = useAuthContext();
  const { invalidateQueries } = useQueryInvalidator();

  return createMutation({
    mutationFn: async ({ partIDs }: CreateSimSandboxesRequest) => {
      const rpcOptions = await getRPCOptions();
      await settleAll(
        partIDs.map(async (partId) => {
          await client.createSimSandbox({ partId }, rpcOptions);
        })
      );
    },
    onSettled: async (_data, _error, { machineID }) => {
      await invalidateQueries([machineKeys.simSandboxes(machineID)]);
    },
  });
};

/** Terminate running simulation sandboxes, attempting all even if some fail. */
export const createDestroySimSandboxesMutation = () => {
  const client = useClient('simSandbox');
  const { getRPCOptions } = useAuthContext();
  const { invalidateQueries } = useQueryInvalidator();

  return createMutation({
    mutationFn: async ({ sandboxIDs }: DestroySimSandboxesRequest) => {
      const rpcOptions = await getRPCOptions();
      await settleAll(
        sandboxIDs.map(async (sandboxId) => {
          await client.destroySimSandbox(
            { sandboxId, reason: DestroyReason.USER_REQUEST },
            rpcOptions
          );
        })
      );
    },
    onSettled: async (_data, _error, { machineID }) => {
      await invalidateQueries([machineKeys.simSandboxes(machineID)]);
    },
  });
};
